import React, { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Monitor } from 'lucide-react';

// ============================================
// 🌙☀️🔄 CONTEXTE THÈME JOUR/NUIT/AUTO
// ============================================

const ThemeContext = createContext({
  isDarkMode: true,
  themeMode: 'auto', // 'dark', 'light', 'auto'
  setThemeMode: () => {},
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Détermine si c'est la nuit (entre 20h et 8h) ou selon préférences système
const shouldBeDark = () => {
  // Vérifier les préférences système en premier
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return true;
  }
  
  // Sinon, basé sur l'heure (nuit = 20h-8h)
  const hour = new Date().getHours();
  return hour >= 20 || hour < 8;
};

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || 'auto'; // 'dark', 'light', 'auto'
  });
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode === 'dark') return true;
    if (savedMode === 'light') return false;
    return shouldBeDark(); // Mode auto
  });

  // Mettre à jour le thème quand le mode change
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    
    if (themeMode === 'dark') {
      setIsDarkMode(true);
    } else if (themeMode === 'light') {
      setIsDarkMode(false);
    } else {
      // Mode auto
      setIsDarkMode(shouldBeDark());
    }
  }, [themeMode]);

  // Écouter les changements de préférences système (mode auto)
  useEffect(() => {
    if (themeMode !== 'auto') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsDarkMode(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Vérifier l'heure toutes les minutes (mode auto)
  useEffect(() => {
    if (themeMode !== 'auto') return;
    
    const checkTime = () => {
      // Ne mettre à jour que si les préférences système ne sont pas définies
      if (!window.matchMedia('(prefers-color-scheme: dark)').matches && 
          !window.matchMedia('(prefers-color-scheme: light)').matches) {
        setIsDarkMode(shouldBeDark());
      }
    };
    
    const interval = setInterval(checkTime, 60000); // Toutes les minutes
    return () => clearInterval(interval);
  }, [themeMode]);

  // Appliquer les classes CSS au body
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
      document.body.style.background = 'linear-gradient(135deg, #0a0015 0%, #1a0a2e 50%, #0d1117 100%)';
      document.body.style.color = '#ffffff';
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
      document.body.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)';
      document.body.style.color = '#1a1a2e';
    }
  }, [isDarkMode]);

  // Toggle simple (cycle: auto -> light -> dark -> auto)
  const toggleTheme = () => {
    if (themeMode === 'auto') {
      setThemeMode('light');
    } else if (themeMode === 'light') {
      setThemeMode('dark');
    } else {
      setThemeMode('auto');
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, themeMode, setThemeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================
// 🎨 SÉLECTEUR DE THÈME 3 OPTIONS
// ============================================

export function ThemeSelector({ className = '' }) {
  const { themeMode, setThemeMode, isDarkMode } = useTheme();
  
  const options = [
    { id: 'light', icon: Sun, label: 'Jour', color: '#fbbf24' },
    { id: 'auto', icon: Monitor, label: 'Auto', color: '#8b5cf6' },
    { id: 'dark', icon: Moon, label: 'Nuit', color: '#6366f1' },
  ];

  return (
    <div 
      className={`flex items-center gap-1 p-1 rounded-full ${className}`}
      style={{
        background: isDarkMode ? 'rgba(30, 27, 75, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        border: isDarkMode ? '2px solid rgba(99, 102, 241, 0.3)' : '2px solid rgba(0, 0, 0, 0.1)',
        boxShadow: isDarkMode ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.1)',
      }}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = themeMode === option.id;
        
        return (
          <motion.button
            key={option.id}
            onClick={() => setThemeMode(option.id)}
            className="relative p-2 rounded-full transition-all"
            style={{
              background: isActive 
                ? `linear-gradient(135deg, ${option.color}30 0%, ${option.color}10 100%)`
                : 'transparent',
              border: isActive ? `1px solid ${option.color}50` : '1px solid transparent',
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title={option.label}
          >
            <Icon 
              className="w-4 h-4 transition-colors"
              style={{ 
                color: isActive ? option.color : (isDarkMode ? '#9ca3af' : '#6b7280')
              }} 
            />
            {isActive && (
              <motion.div
                layoutId="theme-indicator"
                className="absolute inset-0 rounded-full"
                style={{
                  background: `radial-gradient(circle, ${option.color}20 0%, transparent 70%)`,
                }}
                initial={false}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

// ============================================
// 🎨 BOUTON TOGGLE JOUR/NUIT - ÉLÉGANT
// ============================================

export function ThemeToggle({ className = '', size = 'normal' }) {
  const { isDarkMode, themeMode, toggleTheme } = useTheme();
  
  const sizes = {
    small: { button: 'w-14 h-7', icon: 'w-4 h-4', circle: 'w-5 h-5', translate: 'translate-x-7' },
    normal: { button: 'w-16 h-8', icon: 'w-5 h-5', circle: 'w-6 h-6', translate: 'translate-x-8' },
    large: { button: 'w-20 h-10', icon: 'w-6 h-6', circle: 'w-8 h-8', translate: 'translate-x-10' },
  };
  
  const s = sizes[size] || sizes.normal;
  
  // Couleurs selon le mode
  const getColors = () => {
    if (themeMode === 'auto') {
      return {
        bg: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
        shadow: '0 0 20px rgba(139, 92, 246, 0.4)',
        border: '2px solid rgba(139, 92, 246, 0.5)',
      };
    }
    if (isDarkMode) {
      return {
        bg: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
        shadow: '0 0 20px rgba(99, 102, 241, 0.4)',
        border: '2px solid rgba(99, 102, 241, 0.5)',
      };
    }
    return {
      bg: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
      shadow: '0 0 20px rgba(251, 191, 36, 0.5)',
      border: '2px solid rgba(251, 191, 36, 0.5)',
    };
  };
  
  const colors = getColors();
  
  const getTitle = () => {
    if (themeMode === 'auto') return '🔄 Mode Auto (basé sur l\'heure/système)';
    if (isDarkMode) return 'Passer en mode auto 🔄';
    return 'Passer en mode nuit 🌙';
  };

  return (
    <motion.button
      onClick={toggleTheme}
      className={`relative ${s.button} rounded-full p-1 transition-all duration-500 ${className}`}
      style={{
        background: colors.bg,
        boxShadow: colors.shadow,
        border: colors.border,
      }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={getTitle()}
    >
      {/* Indicateur mode auto */}
      {themeMode === 'auto' && (
        <motion.div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-purple-500 flex items-center justify-center"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-[6px] text-white font-bold">A</span>
        </motion.div>
      )}
      
      {/* Étoiles animées (mode nuit) */}
      <AnimatePresence>
        {isDarkMode && themeMode !== 'auto' && (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              className="absolute top-1 right-2 w-1 h-1 bg-white rounded-full"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.7, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ delay: 0.1 }}
              className="absolute top-2 right-4 w-0.5 h-0.5 bg-white rounded-full"
            />
          </>
        )}
      </AnimatePresence>

      {/* Nuages (mode jour) */}
      <AnimatePresence>
        {!isDarkMode && themeMode !== 'auto' && (
          <>
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 0.6, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="absolute top-1 left-2 w-3 h-1.5 bg-white/80 rounded-full"
            />
          </>
        )}
      </AnimatePresence>

      {/* Cercle avec icône */}
      <motion.div
        className={`${s.circle} rounded-full flex items-center justify-center shadow-lg`}
        style={{
          background: themeMode === 'auto'
            ? 'linear-gradient(135deg, #e9d5ff 0%, #c4b5fd 100%)'
            : isDarkMode 
              ? 'linear-gradient(135deg, #c7d2fe 0%, #a5b4fc 100%)'
              : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          boxShadow: themeMode === 'auto'
            ? '0 2px 10px rgba(139, 92, 246, 0.4)'
            : isDarkMode 
              ? '0 2px 10px rgba(0,0,0,0.3)'
              : '0 2px 15px rgba(251, 191, 36, 0.5)',
        }}
        animate={{
          x: themeMode === 'auto' 
            ? parseInt(s.translate.replace('translate-x-', '')) / 2
            : isDarkMode ? 0 : parseInt(s.translate.replace('translate-x-', '')),
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      >
        <AnimatePresence mode="wait">
          {themeMode === 'auto' ? (
            <motion.div
              key="auto"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Monitor className={`${s.icon} text-purple-700`} />
            </motion.div>
          ) : isDarkMode ? (
            <motion.div
              key="moon"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Moon className={`${s.icon} text-indigo-900`} />
            </motion.div>
          ) : (
            <motion.div
              key="sun"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Sun className={`${s.icon} text-amber-600`} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.button>
  );
}

// ============================================
// 🎨 MINI TOGGLE POUR HEADER (3 états)
// ============================================

export function ThemeToggleMini({ className = '' }) {
  const { isDarkMode, themeMode, toggleTheme } = useTheme();
  
  const getStyle = () => {
    if (themeMode === 'auto') {
      return {
        background: 'rgba(139, 92, 246, 0.2)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
      };
    }
    if (isDarkMode) {
      return {
        background: 'rgba(99, 102, 241, 0.2)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
      };
    }
    return {
      background: 'rgba(251, 191, 36, 0.2)',
      border: '1px solid rgba(251, 191, 36, 0.3)',
    };
  };

  const getTitle = () => {
    if (themeMode === 'auto') return '🔄 Auto → ☀️ Jour';
    if (themeMode === 'light') return '☀️ Jour → 🌙 Nuit';
    return '🌙 Nuit → 🔄 Auto';
  };

  return (
    <motion.button
      onClick={toggleTheme}
      className={`relative p-2 rounded-full transition-all ${className}`}
      style={getStyle()}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      title={getTitle()}
    >
      {/* Badge Auto */}
      {themeMode === 'auto' && (
        <motion.div
          className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-purple-500"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
      
      <AnimatePresence mode="wait">
        {themeMode === 'auto' ? (
          <motion.div
            key="auto"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
          >
            <Monitor className="w-4 h-4 text-purple-500" />
          </motion.div>
        ) : isDarkMode ? (
          <motion.div
            key="moon"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
          >
            <Moon className="w-4 h-4 text-indigo-400" />
          </motion.div>
        ) : (
          <motion.div
            key="sun"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
          >
            <Sun className="w-4 h-4 text-amber-500" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

// ============================================
// 🎨 INDICATEUR DE THÈME ACTUEL
// ============================================

export function ThemeIndicator({ className = '' }) {
  const { isDarkMode, themeMode } = useTheme();
  
  const getMessage = () => {
    if (themeMode === 'auto') {
      const hour = new Date().getHours();
      if (hour >= 20 || hour < 8) {
        return '🌙 Mode Auto - Nuit (20h-8h)';
      }
      return '☀️ Mode Auto - Jour (8h-20h)';
    }
    return isDarkMode ? '🌙 Mode Nuit' : '☀️ Mode Jour';
  };

  return (
    <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${className}`}>
      {getMessage()}
    </div>
  );
}

// ============================================
// 🎨 CLASSES CSS CONDITIONNELLES
// ============================================

export const themeClasses = {
  // Backgrounds
  pageBg: (isDark) => isDark 
    ? 'bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0d1117]' 
    : 'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200',
  
  cardBg: (isDark) => isDark 
    ? 'bg-gray-900/80 border-gray-700/50' 
    : 'bg-white/90 border-gray-200 shadow-lg',
  
  cardBgSolid: (isDark) => isDark 
    ? 'bg-gray-900 border-gray-700' 
    : 'bg-white border-gray-200 shadow-lg',
  
  // Text
  textPrimary: (isDark) => isDark ? 'text-white' : 'text-gray-900',
  textSecondary: (isDark) => isDark ? 'text-gray-400' : 'text-gray-600',
  textMuted: (isDark) => isDark ? 'text-gray-500' : 'text-gray-400',
  
  // Inputs
  input: (isDark) => isDark 
    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  
  // Buttons
  buttonPrimary: (isDark) => isDark 
    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
    : 'bg-indigo-500 hover:bg-indigo-600 text-white',
  
  buttonSecondary: (isDark) => isDark 
    ? 'bg-gray-800 hover:bg-gray-700 text-white border-gray-700' 
    : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-300',
};

export default ThemeToggle;
